let activeNamePronunciationAudio: HTMLAudioElement | null = null;
let activeNamePronunciationAudioUrl: string | null = null;
let _cancelPlayback: (() => void) | null = null;

const PCM_CHANNELS = 1;
const PCM_BYTES_PER_SAMPLE = 2;

function decodeBase64ToBytes(audioB64: string): Uint8Array {
  const binary = atob(audioB64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

function writeAscii(view: DataView, offset: number, value: string): void {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
}

function createWaveBlob(audioB64: string, sampleRate: number): Blob {
  const pcmBytes = decodeBase64ToBytes(audioB64);
  const header = new ArrayBuffer(44);
  const view = new DataView(header);
  const dataLength = pcmBytes.byteLength;
  const byteRate = sampleRate * PCM_CHANNELS * PCM_BYTES_PER_SAMPLE;
  const blockAlign = PCM_CHANNELS * PCM_BYTES_PER_SAMPLE;

  writeAscii(view, 0, "RIFF");
  view.setUint32(4, 36 + dataLength, true);
  writeAscii(view, 8, "WAVE");
  writeAscii(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, PCM_CHANNELS, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, PCM_BYTES_PER_SAMPLE * 8, true);
  writeAscii(view, 36, "data");
  view.setUint32(40, dataLength, true);

  return new Blob([header, pcmBytes as BlobPart], { type: "audio/wav" });
}

function clearActiveNamePronunciationAudio(): void {
  if (activeNamePronunciationAudio) {
    activeNamePronunciationAudio.pause();
    activeNamePronunciationAudio.currentTime = 0;
    activeNamePronunciationAudio = null;
  }
  if (activeNamePronunciationAudioUrl) {
    URL.revokeObjectURL(activeNamePronunciationAudioUrl);
    activeNamePronunciationAudioUrl = null;
  }
  if (_cancelPlayback) {
    _cancelPlayback();
    _cancelPlayback = null;
  }
}

export function stopNamePronunciationAudio(): void {
  clearActiveNamePronunciationAudio();
}

export async function playNamePronunciationAudio(
  audioB64: string,
  sampleRate: number,
): Promise<void> {
  clearActiveNamePronunciationAudio();

  const audioUrl = URL.createObjectURL(createWaveBlob(audioB64, sampleRate));
  const audio = new Audio(audioUrl);
  activeNamePronunciationAudio = audio;
  activeNamePronunciationAudioUrl = audioUrl;
  audio.preload = "auto";
  audio.volume = 1;
  (audio as HTMLAudioElement & { playsInline?: boolean }).playsInline = true;

  await new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      _cancelPlayback = null;
      if (activeNamePronunciationAudio === audio) {
        activeNamePronunciationAudio = null;
      }
      if (activeNamePronunciationAudioUrl === audioUrl) {
        URL.revokeObjectURL(audioUrl);
        activeNamePronunciationAudioUrl = null;
      }
      audio.onended = null;
      audio.onerror = null;
    };

    // Allows stopNamePronunciationAudio() to resolve this promise early so callers'
    // finally blocks (spinner cleanup) run even when playback is cancelled.
    _cancelPlayback = () => {
      cleanup();
      resolve();
    };

    audio.onended = () => {
      cleanup();
      resolve();
    };
    audio.onerror = () => {
      cleanup();
      reject(new Error("Unable to play pronunciation audio."));
    };

    audio.play().catch((error) => {
      cleanup();
      reject(error);
    });
  });
}
